import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Loader2, ArrowLeft, AlertTriangle, Printer, Download, Eye, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { QuotationForm } from './QuotationForm'
import { QuotationFormValues } from './schema'
import { 
  getQuotation, 
  createQuotation, 
  updateQuotation 
} from '@/app/services/quotationService'
import { getParty } from '@/app/services/partyService'
import { listSites } from '@/app/services/siteService'
import { listItems } from '@/app/services/itemService'
import { listGodowns } from '@/app/services/inventoryService'
import { Item } from '@/app/types/item'
import { Party } from '@/app/types/party'
import { format } from 'date-fns'
import { DocumentStatusEnum } from '@/app/types/quotation'

// Define PDF data type for quotation
interface QuotationPDFData {
  quotation: any; // Using any for flexibility with PDF template
  partyDetails?: Party;
  companyDetails: {
    name: string;
    address: string;
    cityStatePin: string;
    phone?: string;
    email?: string;
    website?: string;
    logoUrl?: string;
  };
  pdfBuffer?: string;
}

// Use `any` for window.api to simplify type checking temporarily
const api = (window as any).api;

const QuotationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isEditMode = !!id
  
  // State for item search
  const [isSearchingItems, setIsSearchingItems] = useState(false)
  
  // State for PDF operations
  const [isPdfLoading, setIsPdfLoading] = useState(false)

  // Company details for PDF
  const companyDetails = {
    name: 'Your Company Name',
    address: '123 Business Street',
    cityStatePin: 'Your City, State - 123456',
    phone: '+91 98765 43210',
    email: 'info@yourcompany.com',
    website: 'www.yourcompany.com',
    // logoUrl: '/path/to/logo.png',  // Add your logo path
  }

  // Function to search items for product selection in the grid
  const searchItems = async (query: string): Promise<Item[]> => {
    if (!query || query.length < 2) return []
    
    setIsSearchingItems(true)
    try {
      const result = await listItems({ search_query: query, limit: 20 })
      return result.items || []
    } catch (error) {
      console.error('Error searching items:', error)
      return []
    } finally {
      setIsSearchingItems(false)
    }
  }

  // Fetch quotation if in edit mode
  const { 
    data: quotation, 
    isLoading: isLoadingQuotation, 
    isError: isErrorQuotation,
    error: errorQuotation
  } = useQuery({
    queryKey: ['quotation', id],
    queryFn: () => getQuotation(Number(id)),
    enabled: isEditMode,
  })

  // Check if the quotation is in a finalized state
  const isQuotationFinalized = quotation?.status === DocumentStatusEnum.CONFIRMED || 
    quotation?.status === DocumentStatusEnum.CONVERTED || 
    quotation?.status === DocumentStatusEnum.CANCELLED ||
    quotation?.status === DocumentStatusEnum.EXPIRED

  // Fetch party details if we have a party_id from the quotation
  const partyId = quotation?.party_id
  const {
    data: partyDetails,
    isLoading: isLoadingPartyDetails,
  } = useQuery<Party | null>({
    queryKey: ['party-details', partyId],
    queryFn: () => partyId ? getParty(partyId) : null,
    enabled: !!partyId,
  })

  // Fetch sites and other needed data
  const { data: sitesData = { items: [] }, isLoading: isLoadingSites } = useQuery({
    queryKey: ['sites'],
    queryFn: () => listSites({ limit: 100 })
  })

  // Fetch godowns (warehouses) for item selection
  const { data: godownsData = { items: [] }, isLoading: isLoadingGodowns } = useQuery({
    queryKey: ['godowns'],
    queryFn: () => listGodowns({ limit: 100 })
  })

  // Create quotation mutation
  const createMutation = useMutation({
    mutationFn: (data: any) => createQuotation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotations'] })
      toast.success('Quotation created successfully')
      navigate('/quotations/list')
    },
    onError: (error: Error) => {
      toast.error(`Failed to create quotation: ${error.message}`)
    }
  })

  // Update quotation mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => {
      console.log('Starting update mutation with ID:', id)
      return updateQuotation(id, data)
    },
    onSuccess: (data) => {
      console.log('Update mutation succeeded, result:', data)
      console.log('Current ID from useParams:', id)
      
      // First invalidate queries and show success
      console.log('Invalidating queries...')
      queryClient.invalidateQueries({ queryKey: ['quotations'] })
      queryClient.invalidateQueries({ queryKey: ['quotation', id] })
      
      console.log('Showing success toast')
      toast.success('Quotation updated successfully')
      
      console.log(`About to navigate to: /quotations/detail/${id}`)
      
      // Use a slightly longer timeout to ensure all operations complete
      setTimeout(() => {
        console.log(`Navigation via replace to: /quotations/detail/${id}`)
        
        // Use a more forceful redirect with replace
        if (id && id.toString().trim().length > 0) {
          console.log(`ID is valid: "${id}", forcefully replacing location...`)
          window.location.replace(`#/quotations/detail/${id}`)
        } else {
          console.error(`Invalid ID for navigation: "${id}"`)
          window.location.replace('#/quotations/list')
        }
      }, 300) // Use a longer timeout to ensure completion
    },
    onError: (error: Error) => {
      console.error('Update mutation failed with error:', error)
      toast.error(`Failed to update quotation: ${error.message}`)
    }
  })

  // Handle form submission
  const handleSubmit = (data: QuotationFormValues): void => {
    if (isQuotationFinalized) {
      toast.error('Cannot update a finalized quotation')
      return
    }

    try {
      // Convert form data to API format
      const apiData = {
        site_id: data.site_id,
        party_id: data.party_id,
        reference_number: data.reference_number,
        notes: data.notes,
        terms_and_conditions: data.terms_and_conditions,
        // Convert dates to YYYY-MM-DD format for API
        transaction_date: data.transaction_date instanceof Date 
          ? format(data.transaction_date, 'yyyy-MM-dd')
          : data.transaction_date,
        valid_until_date: data.valid_until_date instanceof Date 
          ? format(data.valid_until_date, 'yyyy-MM-dd')
          : data.valid_until_date,
        status: DocumentStatusEnum.DRAFT,
        // Process items to ensure they have all required fields
        items: data.items.map((item) => ({
          item_id: item.item_id,
          description: item.description || '',
          quantity: item.quantity,
          unit_id: item.unit_id || 1, // Use unit_id from item or default to 1
          rate: item.rate,
          discount_percent: item.discount_percent || 0,
          discount_amount: item.discount_amount || 0,
          tax_rate_id: item.tax_rate_id,
          godown_id: item.godown_id,
          // Remove calculated fields - let backend calculate
          // taxable_amount, tax_amount, total_amount are calculated by backend
        }))
      }
      
      console.log('Submitting quotation data:', JSON.stringify(apiData, null, 2))
      
      if (isEditMode && id) {
        updateMutation.mutate({ id: Number(id), data: apiData })
      } else {
        createMutation.mutate(apiData)
      }
    } catch (error) {
      console.error('Error during submission:', error)
      toast.error(`Submission error: ${(error as Error).message}`)
    }
  }

  // Handle errors consistently
  useEffect(() => {
    if (isErrorQuotation) {
      toast.error(`Error loading quotation: ${(errorQuotation as Error).message}`)
    }
  }, [isErrorQuotation, errorQuotation])

  // PDF Generation and Handling Functions
  
  const generatePDFData = (base64pdf: string): QuotationPDFData | null => {
    if (!quotation) return null
    return {
      quotation,
      partyDetails: partyDetails || undefined,
      companyDetails,
      pdfBuffer: base64pdf
    }
  }
  
  const getBase64PDF = async (): Promise<string | null> => {
    if (!quotation) return null

    // For now, we'll just show a message since QuotationPDFTemplate doesn't exist yet
    toast.info('Quotation PDF template is not yet implemented')
    return null
  }
  
  // Save PDF
  const handleSavePDF = async (): Promise<void> => {
    toast.info('PDF functionality will be available soon')
  }
  
  // Print PDF
  const handlePrintPDF = async (): Promise<void> => {
    toast.info('PDF functionality will be available soon')
  }
  
  // Preview PDF
  const handlePreviewPDF = async (): Promise<void> => {
    toast.info('PDF functionality will be available soon')
  }

  // Loading state
  const isLoading =
    isLoadingQuotation ||
    isLoadingSites ||
    isLoadingGodowns ||
    isLoadingPartyDetails
  const isSubmitting = createMutation.isPending || updateMutation.isPending
  const canGeneratePdf = isEditMode && quotation && !isLoading

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/quotations/list')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">
            {isEditMode ? `Edit Quotation ${quotation?.document_number}` : 'Create Quotation'}
          </h1>
        </div>
        
        {/* PDF Action Buttons */}
        {canGeneratePdf && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handlePreviewPDF} disabled={isPdfLoading}>
              {isPdfLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Eye className="h-4 w-4 mr-2" />
              )}
              Preview
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrintPDF} disabled={isPdfLoading}>
              {isPdfLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Printer className="h-4 w-4 mr-2" />
              )}
              Print
            </Button>
            <Button variant="default" size="sm" onClick={handleSavePDF} disabled={isPdfLoading}>
              {isPdfLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Save PDF
            </Button>
          </div>
        )}
      </div>

      {isErrorQuotation && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Error loading quotation: {(errorQuotation as Error).message}
          </AlertDescription>
        </Alert>
      )}

      {godownsData.items.length === 0 && !isLoadingGodowns && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            No warehouses found. Please create at least one warehouse before creating quotations.
          </AlertDescription>
        </Alert>
      )}

      {/* Show warning if quotation is finalized */}
      {isQuotationFinalized && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            This quotation has been{' '}
            {quotation?.status === DocumentStatusEnum.CANCELLED
              ? 'cancelled'
              : quotation?.status === DocumentStatusEnum.CONVERTED
                ? 'converted to sales order'
                : quotation?.status === DocumentStatusEnum.EXPIRED
                  ? 'expired'
                  : 'confirmed'}{' '}
            and cannot be modified.
          </AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : godownsData.items.length > 0 ? (
        <QuotationForm
          key={id || 'new'} // Force re-render when ID changes
          quotation={quotation}
          sites={sitesData.items}
          godowns={godownsData.items}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
          searchItems={searchItems}
          isLoadingItems={isSearchingItems}
          isDisabled={isQuotationFinalized}
        />
      ) : null}
    </div>
  )
}

export default QuotationDetailPage 